import * as THREE from 'three'

export interface OrientationData {
  center: { x: number; y: number; z: number }
  frontAxis: { x: number; y: number; z: number }
  upAxis: { x: number; y: number; z: number }
  frontExtent: number
  upExtent: number
}

export const computeLegacyOrientationData = (geometry: THREE.BufferGeometry): OrientationData | null => {
  const pos = geometry.getAttribute('position')
  if (!pos || pos.count < 3) return null

  const points: number[][] = []
  let cx = 0
  let cy = 0
  let cz = 0

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i)
    const y = pos.getY(i)
    const z = pos.getZ(i)
    points.push([x, y, z])
    cx += x
    cy += y
    cz += z
  }

  cx /= pos.count
  cy /= pos.count
  cz /= pos.count

  // Covariance matrix of centered points.
  let c00 = 0; let c01 = 0; let c02 = 0
  let c11 = 0; let c12 = 0; let c22 = 0
  for (const p of points) {
    const x = p[0] - cx
    const y = p[1] - cy
    const z = p[2] - cz
    c00 += x * x
    c01 += x * y
    c02 += x * z
    c11 += y * y
    c12 += y * z
    c22 += z * z
  }
  const invN = 1 / points.length
  const m = [
    [c00 * invN, c01 * invN, c02 * invN],
    [c01 * invN, c11 * invN, c12 * invN],
    [c02 * invN, c12 * invN, c22 * invN]
  ]

  const normalize = (v: number[]) => {
    const n = Math.hypot(v[0], v[1], v[2]) || 1
    return [v[0] / n, v[1] / n, v[2] / n]
  }
  const dot = (a: number[], b: number[]) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
  const matVec = (mat: number[][], v: number[]) => ([
    mat[0][0] * v[0] + mat[0][1] * v[1] + mat[0][2] * v[2],
    mat[1][0] * v[0] + mat[1][1] * v[1] + mat[1][2] * v[2],
    mat[2][0] * v[0] + mat[2][1] * v[1] + mat[2][2] * v[2]
  ])
  const sub = (a: number[], b: number[]) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
  const mul = (v: number[], s: number) => [v[0] * s, v[1] * s, v[2] * s]

  const powerIteration = (mat: number[][], seed: number[], orthTo?: number[]) => {
    let v = normalize(seed)
    for (let i = 0; i < 18; i++) {
      let av = matVec(mat, v)
      if (orthTo) {
        av = sub(av, mul(orthTo, dot(av, orthTo)))
      }
      v = normalize(av)
    }
    const av = matVec(mat, v)
    const lambda = dot(v, av)
    return { v, lambda }
  }

  const e1 = powerIteration(m, [1, 0.4, 0.2])
  const deflated = [
    [m[0][0] - e1.lambda * e1.v[0] * e1.v[0], m[0][1] - e1.lambda * e1.v[0] * e1.v[1], m[0][2] - e1.lambda * e1.v[0] * e1.v[2]],
    [m[1][0] - e1.lambda * e1.v[1] * e1.v[0], m[1][1] - e1.lambda * e1.v[1] * e1.v[1], m[1][2] - e1.lambda * e1.v[1] * e1.v[2]],
    [m[2][0] - e1.lambda * e1.v[2] * e1.v[0], m[2][1] - e1.lambda * e1.v[2] * e1.v[1], m[2][2] - e1.lambda * e1.v[2] * e1.v[2]]
  ]
  const e2 = powerIteration(deflated, [0.3, 1, 0.2], e1.v)
  const v1 = new THREE.Vector3(e1.v[0], e1.v[1], e1.v[2]).normalize()
  const v2 = new THREE.Vector3(e2.v[0], e2.v[1], e2.v[2]).normalize()
  const v3 = new THREE.Vector3().crossVectors(v1, v2).normalize()

  const axes = [v1, v2, v3]
  const extents = axes.map((axis) => {
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const p of points) {
      const centered = new THREE.Vector3(p[0] - cx, p[1] - cy, p[2] - cz)
      const proj = centered.dot(axis)
      if (proj < min) min = proj
      if (proj > max) max = proj
    }
    return max - min
  })

  // Legacy mapping:
  // - front/back axis = largest extent
  // - top/bottom axis = smallest extent
  const frontIdx = extents.indexOf(Math.max(...extents))
  const upIdx = extents.indexOf(Math.min(...extents))
  const front = axes[frontIdx]
  const up = axes[upIdx]

  return {
    center: { x: cx, y: cy, z: cz },
    frontAxis: { x: front.x, y: front.y, z: front.z },
    upAxis: { x: up.x, y: up.y, z: up.z },
    frontExtent: extents[frontIdx],
    upExtent: extents[upIdx]
  }
}
